/**
 * v3.21-1718 · Ghost Coach Standards Editor
 *
 * Admin/management UI for configuring role-based Green/Yellow/Red
 * thresholds.  Historical snapshots retain the standards that were in
 * effect at snapshot time — changes here NEVER rewrite prior history.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, ShieldCheck, History } from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '../hooks/useDocumentTitle';

const ROLES = ['sales_rep', 'ptc_rep', 'ptc_team_member', 'ptc_manager'];
const money = n => n == null ? '—' : `$${Number(n).toLocaleString(undefined,{maximumFractionDigits:0})}`;
const fmtValue = (v, format) => {
  if (v === null || v === undefined) return '—';
  if (format === 'currency') return money(v);
  if (format === 'percent') return `${v}%`;
  if (format === 'duration_min') return `${v} min`;
  if (format === 'duration_sec') return `${v} sec`;
  return String(v);
};

function StandardRow({ std, meta, onSave }) {
  const [g, setG] = useState(std.green);
  const [y, setY] = useState(std.yellow);
  const [d, setD] = useState(std.direction);
  const [en, setEn] = useState(std.enabled !== false);
  const [saving, setSaving] = useState(false);
  const format = meta?.format || 'number';
  const dirty = g !== std.green || y !== std.yellow || d !== std.direction || en !== (std.enabled !== false);

  const save = async () => {
    setSaving(true);
    try {
      if (std.id) {
        await api.patch(`/ghost-coach/standards/${std.id}`, {
          green: Number(g), yellow: Number(y), direction: d, enabled: en,
        });
      } else {
        await api.post('/ghost-coach/standards', {
          metric_key: std.metric_key, role: std.role,
          direction: d, green: Number(g), yellow: Number(y), red: Number(y),
          enabled: en,
        });
      }
      toast.success('Standard saved — historical snapshots unchanged.');
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <tr className="border-b" data-testid={`std-row-${std.metric_key}`}>
      <td className="py-2 pr-2 font-semibold text-xs">{meta?.label || std.metric_key}</td>
      <td className="pr-2 text-[11px] text-slate-500">{meta?.unit}</td>
      <td className="pr-2">
        <Select value={d} onValueChange={setD}>
          <SelectTrigger className="h-7 text-xs w-[140px]" data-testid={`std-dir-${std.metric_key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="higher_is_better">Higher is Better</SelectItem>
            <SelectItem value="lower_is_better">Lower is Better</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="pr-2">
        <Input type="number" value={g} onChange={e=>setG(e.target.value)} className="h-7 w-[100px] text-xs" data-testid={`std-green-${std.metric_key}`} />
        <div className="text-[10px] text-slate-400 mt-0.5">Preview: {fmtValue(g, format)}</div>
      </td>
      <td className="pr-2">
        <Input type="number" value={y} onChange={e=>setY(e.target.value)} className="h-7 w-[100px] text-xs" data-testid={`std-yellow-${std.metric_key}`} />
        <div className="text-[10px] text-slate-400 mt-0.5">Preview: {fmtValue(y, format)}</div>
      </td>
      <td className="pr-2">
        <input type="checkbox" checked={en} onChange={e=>setEn(e.target.checked)} data-testid={`std-enabled-${std.metric_key}`} />
      </td>
      <td className="pr-2 text-[10px] text-slate-500">
        {std.source === 'default' ? <Badge variant="outline">Default</Badge>
                                  : (std.effective_date || '—').slice(0,10)}
      </td>
      <td>
        <Button size="sm" onClick={save} disabled={!dirty || saving} className="h-7 text-xs" data-testid={`std-save-${std.metric_key}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save</>}
        </Button>
      </td>
    </tr>
  );
}

export default function StandardsEditorPage() {
  useDocumentTitle('Ghost Coach — Standards Editor');
  const [role, setRole] = useState('sales_rep');
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: a }] = await Promise.all([
        api.get('/ghost-coach/standards', { params: { role } }),
        api.get('/ghost-coach/standards/audit', { params: { limit: 30 } }),
      ]);
      setData(d); setAudit(a.audit || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const rows = useMemo(() => {
    if (!data) return [];
    return [...(data.active || []), ...(data.defaults || [])]
      .filter(r => r.role === role);
  }, [data, role]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-emerald-500" />
        <div>
          <div className="text-lg font-bold">Ghost Coach — Standards Editor</div>
          <div className="text-xs text-slate-500">
            Changes take effect immediately for current performance.  Historical snapshots retain
            the standards that were in effect at snapshot time.
          </div>
        </div>
        <div className="flex-1" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="std-role-picker">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card><CardContent className="p-4 overflow-x-auto">
        {loading ? <div className="p-4 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div> : (
          <table className="w-full text-xs" data-testid="standards-table">
            <thead>
              <tr className="border-b text-left text-[10px] uppercase text-slate-400">
                <th className="py-1 pr-2">Metric</th><th className="pr-2">Unit</th>
                <th className="pr-2">Direction</th>
                <th className="pr-2">Above (Green)</th><th className="pr-2">At (Yellow)</th>
                <th className="pr-2">Enabled</th><th className="pr-2">Effective</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <StandardRow key={`${r.role}-${r.metric_key}`} std={r} meta={data.meta?.[r.metric_key]} onSave={load} />
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <History className="h-3.5 w-3.5" /> Standards Audit History
        </div>
        <div className="max-h-[240px] overflow-y-auto" data-testid="std-audit-list">
          {audit.length === 0 ? <div className="text-xs text-slate-400">No changes recorded.</div> :
            audit.map((a, i) => (
              <div key={i} className="border-b py-1 text-xs">
                <div className="flex justify-between">
                  <span className="font-semibold">{a.type.replace('ghost_coach_standard_','').toUpperCase()}</span>
                  <span className="text-slate-400">{(a.created_at || '').slice(0,19).replace('T',' ')}</span>
                </div>
                <div className="text-slate-500">by {a.actor}</div>
                {a.payload && <pre className="text-[10px] mt-1 bg-slate-50 p-1 rounded">{JSON.stringify(a.payload, null, 1)}</pre>}
                {a.before && <div className="text-[10px] mt-1">before: <code>{JSON.stringify(a.before)}</code></div>}
                {a.after && <div className="text-[10px]">after: <code>{JSON.stringify(a.after)}</code></div>}
              </div>
            ))
          }
        </div>
      </CardContent></Card>
    </div>
  );
}
